import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  BIZ_MODEL_HP_PLUS,
  DEVICE_SEGMENT_LFP,
  DUNE_DESIGN_JET,
  MULTI_FUNCTION_PRINTER,
  OFFER_HINT_SINGLE_SKU,
  SINGLE_FUNCTION_PRINTER,
  SINGLE_SKU_OFFER
} from './Constants'
import { ConfigContext } from '@/store'
import { EMPTY_FUNCTION, stringEquals } from '@/utils/Functions'
import { useFlags } from 'launchdarkly-react-client-sdk'

export const PrinterContext = React.createContext({
  claimPostcard: null,
  cloudId: null,
  productNumber: null,
  fingerprint: null,
  bizModel: null,
  uuid: null,
  serialNumber: null,
  pairingCode: null,
  pollingInterval: null,
  claimTimestamp: null,
  isHpPlus: false,
  printerImage: null,
  name: null,
  modelName: null,
  location: null,
  deviceCapabilities: null,
  isSingleSku: null,
  offer: null,
  isMono: true,
  deviceSegment: true,
  connectivityFamily: false,
  oid: null,
  isLargeFormat: true,
  setClaimPostcard: EMPTY_FUNCTION,
  setCloudId: EMPTY_FUNCTION,
  setProductNumber: EMPTY_FUNCTION,
  setFingerprint: EMPTY_FUNCTION,
  setBizModel: EMPTY_FUNCTION,
  setUuid: EMPTY_FUNCTION,
  setSerialNumber: EMPTY_FUNCTION,
  setPairingCode: EMPTY_FUNCTION,
  setPollingInterval: EMPTY_FUNCTION,
  setClaimTimestamp: EMPTY_FUNCTION,
  setPrinterImage: EMPTY_FUNCTION,
  setName: EMPTY_FUNCTION,
  setModelName: EMPTY_FUNCTION,
  setLocation: EMPTY_FUNCTION,
  setDeviceCapabilities: EMPTY_FUNCTION,
  setOffer: EMPTY_FUNCTION,
  setIsMono: EMPTY_FUNCTION,
  setDeviceSegment: EMPTY_FUNCTION,
  setConnectivityFamily: EMPTY_FUNCTION,
  setOid: EMPTY_FUNCTION,
  setPrinterFunctionType: EMPTY_FUNCTION,
  printerFunctionType: null,
  isPartnerLinkSupported: false
})

const PrinterProvider = (props) => {
  const {
    getStoreData,
    setStoreData,
    sessionContext,
    updateStageContext
  } = useContext(ConfigContext)
  const { onbpReleaseLfpverification: isLfpEnabled } = useFlags()

  const [claimPostcard, setClaimPostcard] = useState(null)
  const [cloudId, setCloudId] = useState(null)
  const [productNumber, setProductNumber] = useState(null)
  const [fingerprint, setFingerprint] = useState(null)
  const [bizModel, setBizModel] = useState(null)
  const [uuid, setUuid] = useState(null)
  const [serialNumber, setSerialNumber] = useState(null)
  const [pairingCode, setPairingCode] = useState(null)
  const [pollingInterval, setPollingInterval] = useState(5)
  const [claimTimestamp, setClaimTimestamp] = useState(null)
  const [printerImage, setPrinterImage] = useState(null)
  const [loadedFromStore, setLoadedFromStore] = useState(false)
  const [name, setName] = useState(null)
  const [modelName, setModelName] = useState(null)
  const [location, setLocation] = useState(null)
  const [deviceCapabilities, setDeviceCapabilities] = useState(null)
  const [isSingleSku, setIsSingleSku] = useState(null)
  const [offer, setOffer] = useState(null)
  const [isMono, setIsMono] = useState(null)
  const [deviceSegment, setDeviceSegment] = useState(null)
  const [connectivityFamily, setConnectivityFamily] = useState(null)
  const [oid, setOid] = useState(null)
  const [printerFunctionType, setPrinterFunctionType] = useState(null)

  const isPartnerLinkSupported = useMemo(
    () =>
      sessionContext?.derivedAttributes?.pls?.partnerLinkCapability ===
      'supported',
    [sessionContext]
  )

  const resetPrinterData = () => {
    setBizModel(null)
    setPollingInterval(null)
    setClaimPostcard(null)
    setCloudId(null)
    setProductNumber(null)
    setFingerprint(null)
    setUuid(null)
    setSerialNumber(null)
    setPairingCode(null)
    setClaimTimestamp(null)
    setPrinterImage(null)
    setName(null)
    setModelName(null)
    setLocation(null)
    setDeviceCapabilities(null)
    setIsSingleSku(null)
    setOffer(null)
    setIsMono(null)
    setDeviceSegment(null)
    setConnectivityFamily(null)
    setOid(null)
  }

  useEffect(() => {
    if (!loadedFromStore) {
      const printerData = getStoreData('printer')
      if (printerData) {
        setClaimPostcard(printerData.claimPostcard)
        setCloudId(printerData.cloudId)
        setProductNumber(printerData.productNumber)
        setFingerprint(printerData.fingerprint)
        setBizModel(printerData.bizModel)
        setUuid(printerData.uuid)
        setSerialNumber(printerData.serialNumber)
        setPairingCode(printerData.pairingCode)
        setPollingInterval(printerData.pollingInterval)
        setClaimTimestamp(printerData.claimTimestamp)
        setPrinterImage(printerData.printerImage)
        setName(printerData.name)
        setModelName(printerData.modelName)
        setLocation(printerData.location)
        setDeviceCapabilities(printerData.deviceCapabilities)
        setIsSingleSku(printerData.isSingleSku)
        setOffer(printerData.offer)
        setIsMono(printerData.isMono)
        setDeviceSegment(printerData.deviceSegment)
        setConnectivityFamily(printerData.connectivityFamily)
        setOid(printerData.oid)
        setPrinterFunctionType(printerData.printerFunctionType)
      }
      setLoadedFromStore(true)
    }
  }, [getStoreData, loadedFromStore])

  useEffect(() => {
    const _isSingleSku =
      sessionContext?.onboardingContext?.offerHint === OFFER_HINT_SINGLE_SKU ||
      sessionContext?.device?.offer === SINGLE_SKU_OFFER

    setIsSingleSku(_isSingleSku)

    if (!deviceCapabilities) return

    const hasFaxSupport = deviceCapabilities.some(
      (c) => c.name === 'fax' && c.support === 'true'
    )

    const hasScanSupport = deviceCapabilities.some(
      (c) => c.name === 'scan' && c.support === 'true'
    )

    setPrinterFunctionType(
      hasFaxSupport && hasScanSupport
        ? MULTI_FUNCTION_PRINTER
        : SINGLE_FUNCTION_PRINTER
    )
  }, [deviceCapabilities, sessionContext])

  useEffect(() => {
    if (loadedFromStore) {
      setStoreData('printer', {
        claimPostcard,
        cloudId,
        productNumber,
        fingerprint,
        bizModel,
        uuid,
        serialNumber,
        pairingCode,
        pollingInterval,
        claimTimestamp,
        printerImage,
        name,
        modelName,
        location,
        deviceCapabilities,
        isSingleSku,
        offer,
        isMono,
        deviceSegment,
        connectivityFamily,
        oid,
        printerFunctionType
      })
      updateStageContext({
        cloudId,
        bizModel,
        printerImage,
        name,
        modelName,
        location
      })
    }
  }, [
    claimPostcard,
    cloudId,
    productNumber,
    fingerprint,
    bizModel,
    uuid,
    serialNumber,
    pairingCode,
    pollingInterval,
    claimTimestamp,
    printerImage,
    loadedFromStore,
    name,
    modelName,
    location,
    setStoreData,
    deviceCapabilities,
    isSingleSku,
    offer,
    isMono,
    deviceSegment,
    connectivityFamily,
    oid,
    updateStageContext,
    printerFunctionType
  ])

  const hasDesignJetConnectivity = () =>
    !isLfpEnabled &&
    connectivityFamily?.some?.((c) => stringEquals(c, DUNE_DESIGN_JET))

  const isLFP = () => stringEquals(deviceSegment, DEVICE_SEGMENT_LFP)

  const checkIsLargeFormat = () => hasDesignJetConnectivity() || isLFP()

  const printerState = {
    claimPostcard,
    cloudId,
    productNumber: sessionContext?.device?.productNumber || productNumber,
    fingerprint,
    bizModel,
    uuid,
    serialNumber,
    pairingCode,
    pollingInterval,
    claimTimestamp,
    printerImage,
    deviceCapabilities,
    isSingleSku,
    offer,
    isMono,
    deviceSegment,
    connectivityFamily,
    oid,
    isLargeFormat: checkIsLargeFormat(),
    isHpPlus: stringEquals(
      sessionContext?.device?.bizModel || bizModel,
      BIZ_MODEL_HP_PLUS
    ),
    name,
    modelName: sessionContext?.device?.modelName || modelName,
    location,
    setClaimPostcard,
    setCloudId,
    setProductNumber,
    setFingerprint,
    setBizModel,
    setUuid,
    setSerialNumber,
    setPairingCode,
    setDeviceCapabilities,
    setPrinterFunctionType,
    setPollingInterval,
    setClaimTimestamp,
    setPrinterImage,
    setName,
    setModelName,
    setLocation,
    setOffer,
    setIsMono,
    setDeviceSegment,
    setConnectivityFamily,
    setOid,
    resetPrinterData,
    printerFunctionType,
    isPartnerLinkSupported,
    loadedFromStore
  }

  return (
    <PrinterContext.Provider value={printerState}>
      {props.children}
    </PrinterContext.Provider>
  )
}

export default PrinterProvider
